import {faArrowLeft} from "@fortawesome/free-solid-svg-icons"
import {useEffect, useState} from "react"
import {createUseStyles} from "react-jss"
import {Link, useNavigate, useParams} from "react-router-dom"
import Button from "../components/Button"
import CardSection from "../components/CardSection"
import ConfirmModal from "../components/ConfirmModal"
import DetailsCard from "../components/DetailsCard"
import FullScreenLoadingIndicator from "../components/FullScreenLoadingIndicator"
import TextAreaField from "../components/TextAreaField";
import NavLink from "../components/NavLink"
import {titleMarginBottom} from "../constants/layout"
import useProtectIssueActions from "../hooks/useProtectIssueActions"
import useProtectCustomerActions from "../hooks/useProtectCustomerActions"
import {useAppSelector} from "../redux/hooks"
import {formatDateTimeString} from "../utils/dateHelper"
import {ProtectIssueDecisionType, UpdateProtectIssueDecisionModel, UpdateProtectIssueInvestigationModel} from "../redux/types";
import Select from "../components/Select";
import Switch from "../components/Switch"
import DatePicker from "../components/DatePicker"

const ProtectIssueDetails = () => {
  const styles = useStyles()
  const params = useParams<{ id: string }>()
  const navigate = useNavigate();
  const {getIssue, updateIssueInvestigation, updateIssueDecision} = useProtectIssueActions()
  const {getProtectCustomers} = useProtectCustomerActions()

  const loading = useAppSelector(state => state.protectIssues.loading)
  const issueDetails = useAppSelector(state => state.protectIssues.issueDetails)
  const customers = useAppSelector(state => state.protectCustomer.allCustomers);

  const [investigation, setInvestigation] = useState<UpdateProtectIssueInvestigationModel>()
  const [decision, setDecision] = useState<UpdateProtectIssueDecisionModel>()
  const [confirmCloseIssueModalVisible, setConfirmCloseIssueModalVisible] = useState(false)
  const [confirmCloseInvestigationModalVisible, setConfirmCloseInvestigationModalVisible] = useState(false)

  useEffect(() => {
    if (issueDetails == null || issueDetails.id !== params.id) {
      if (params.id) {
        getIssue(params.id)
      }
    }

    if (!customers || customers.length === 0) {
      getProtectCustomers()
    }
  })

  useEffect(() => {
    if (issueDetails) {
      setInvestigation({
        customerId: issueDetails.customerId,
        investigationResult: issueDetails.investigationResult,
        investigationDone: issueDetails.investigationDone,
        isSuspectedFraud: issueDetails.isSuspectedFraud,
        isSuspectedMlOrTf: issueDetails.isSuspectedMlOrTf,
      })
      setDecision({
        decision: issueDetails.decision,
        actions: issueDetails.actions,
        fipoReportDate: issueDetails.fipoReportDate,
        issueClosed: issueDetails.closed
      })
    }
  }, [issueDetails])

  const saveDecisionAndCloseIssue = () => {
    setConfirmCloseIssueModalVisible(true)
  }

  const saveDecision = () => {
    if (issueDetails && decision)
      updateIssueDecision(issueDetails?.id, decision)
  }

  const onCloseIssueConfirmed = () => {
    if (issueDetails && decision) {
      let closedIssue = {...decision, issueClosed: true}
      updateIssueDecision(issueDetails?.id, closedIssue)
    }
    setConfirmCloseIssueModalVisible(false)
  }

  const saveAndCloseInvestigation = () => {
    setConfirmCloseInvestigationModalVisible(true)
  }

  const onCloseInvestigationConfirmed = () => {
    if (issueDetails && investigation) {
      let closedInvestigation = {...investigation, investigationDone: true}
      updateIssueInvestigation(issueDetails?.id, closedInvestigation)
    }
    setConfirmCloseInvestigationModalVisible(false)
  }

  const saveInvestigation = () => {
    if (issueDetails && investigation)
      updateIssueInvestigation(issueDetails?.id, investigation)
  }

  const handleInvestigationChange = (propName: string, value: any) => {
    if (investigation) {
      setInvestigation({...investigation, [propName]: value === '' ? null : value})
    }
  }

  const handleDecisionChange = (propName: string, value: any) => {
    if (decision) {
      setDecision({...decision, [propName]: value === '' ? null : value})
    }
  }

  const getCustomerOptions = () => {
    return [{name: "Unknown", value: ''}].concat(
      customers.map(x => {
        return {name: x.name, value: x.id}
      })
    )
  }

  const getDecisionOptions = () => {
      return Object.keys(ProtectIssueDecisionType).filter(v => isNaN(Number(v)))
          .map((value) => { return { name: value, value: value }
      })
  }

  const renderClosedInvestigationResult = () => {
    if (issueDetails?.closed || issueDetails?.investigationDone) {
      return (
        <div>
          <p>Result: <span>{issueDetails.investigationResult}</span></p>
          <p>Suspected money laundering or terrorism financing: <span>{issueDetails.isSuspectedMlOrTf ? 'yes' : 'no'}</span></p>
          <p>Suspected fraud: <span>{issueDetails.isSuspectedFraud ? 'yes' : 'no'}</span></p>
          <p>Investigated at: <span>{formatDateTimeString(issueDetails.investigationDate ?? undefined)}</span></p>
        </div>)
    }
  }

  const renderClosedDecisionResult = () => {
    if (issueDetails?.closed) {
      return (
        <div>
          <p>Decision: <span>{issueDetails.decision}</span></p>
          {issueDetails.decision == ProtectIssueDecisionType.fipoReport &&
          <p>FIPO report date: <span>{formatDateTimeString(issueDetails.fipoReportDate)}</span></p>
          }
          <p>Actions: <span>{issueDetails.actions}</span></p>
          <p>Closed at: <span>{formatDateTimeString(issueDetails.closedDate ?? undefined)}</span></p>
        </div>)
    }
  }

  const renderInvestigationContainer = () => {
    if (issueDetails && !issueDetails?.closed && !issueDetails?.investigationDone) {
      return (
        <div className={styles.investigationContainer}>
          <TextAreaField
            id='investigationResult'
            value={investigation?.investigationResult ?? ''}
            onChange={(event) => handleInvestigationChange('investigationResult', event.target.value)}
            placeholder='Enter your investigation result here'
            label=''
          />
          <div>
            <Switch  
              id='isSuspectedMlOrTf'
              key='isSuspectedMlOrTf'
              checked={investigation?.isSuspectedMlOrTf ?? false}
              label='Suspected money laundering or terrorism financing'
              onChange={() => handleInvestigationChange('isSuspectedMlOrTf', !(investigation?.isSuspectedMlOrTf ?? false))} />

            <Switch  
              id='isSuspectedFraud'
              key='isSuspectedFraud'
              checked={investigation?.isSuspectedFraud ?? false}
              label='Suspected fraud'
              onChange={() => handleInvestigationChange('isSuspectedFraud', !(investigation?.isSuspectedFraud ?? false))} />
          </div>
          {issueDetails.isGlobalRule &&
          <Select
            id='customer'
            key='customer'
            label='Customer'
            options={getCustomerOptions()}
            selectedValue={issueDetails.customerId || ''}
            onSelect={(value) => handleInvestigationChange('customerId', value)}
          />}
          <div className={styles.buttonContainer}>
            <Button title={'Save and close investigation'} onClick={saveAndCloseInvestigation}
                    disabled={!investigation?.customerId || !investigation?.investigationResult}/>
            <Button title={'Save investigation'} onClick={saveInvestigation} disabled={!investigation?.investigationResult}/>
          </div>
        </div>
      )
    } else {
      return renderClosedInvestigationResult()
    }
  }

  const renderDecisionContainer = () => {
     if (!issueDetails?.investigationDone) {
      return <></>  
    }

    if (issueDetails && issueDetails?.closed) {
      return renderClosedDecisionResult()
    }

    return (
      <div className={styles.investigationContainer}>
        <Select
          id='decision'
          key='decision'
          label='Decision'
          options={getDecisionOptions()}
          selectedValue={decision?.decision.toString()}
          onSelect={(value) => handleDecisionChange('decision', ProtectIssueDecisionType[value as keyof typeof ProtectIssueDecisionType])}
        />
        {decision?.decision === ProtectIssueDecisionType.fipoReport && 
        <DatePicker
          id='fipoReportDate'
          label='FIPO report date'
          includeTime={false}
          value={decision?.fipoReportDate}
          onChange={value => { handleDecisionChange('fipoReportDate', value)}}
        />
        }

        <TextAreaField
          id='Actions'
          value={decision?.actions ?? ''}
          onChange={(event) => handleDecisionChange('actions', event.target.value)}
          placeholder=''
          label='Actions taken'
        />
        <div className={styles.buttonContainer}>
          <Button title={'Save and close issue'} 
            onClick={saveDecisionAndCloseIssue}
            disabled={decision?.decision === ProtectIssueDecisionType.fipoReport && !decision?.fipoReportDate} />
          <Button title={'Save'} 
            onClick={saveDecision} 
            disabled={decision?.decision === ProtectIssueDecisionType.fipoReport && !decision?.fipoReportDate} />
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <h1>{issueDetails && `${issueDetails.customerName || issueDetails.ruleName} - ${formatDateTimeString(issueDetails.createdDate)}`}</h1>
      <div className={styles.actionBar}>
        <NavLink title='Back' icon={faArrowLeft} onClick={() => navigate(-1)}/>
      </div>
      <CardSection>
        <DetailsCard>
          <h6>Details</h6>
          <p>Domain: <span>{issueDetails?.domain}</span></p>
          <p>Customer: <span>{issueDetails?.customerName ?? 'Unknown'}</span></p>
          <p>Description: <span>{issueDetails?.description}</span></p>
          <Link to={{pathname: `/protect/rules/${issueDetails?.ruleId}`}}><p>Rule: <span>{issueDetails?.ruleName}</span>
          </p></Link>
          <p>State: <span>{issueDetails?.closed ? 'Closed' : 'Open'}</span></p>
        </DetailsCard>
      </CardSection>
      <CardSection>
        <DetailsCard>
          <h6>Investigation</h6>
          {renderInvestigationContainer()}
        </DetailsCard>
      </CardSection>
      <CardSection>
        <DetailsCard>
          <h6>Decision</h6>
          {renderDecisionContainer()}
        </DetailsCard>
      </CardSection>
      <FullScreenLoadingIndicator visible={loading}/>
      <ConfirmModal
        visible={confirmCloseInvestigationModalVisible}
        confirmText={'Are you sure you want to close the investigation of this issue?'}
        onConfirmClick={onCloseInvestigationConfirmed}
        onCancelClick={() => setConfirmCloseInvestigationModalVisible(false)}
      />
      <ConfirmModal
        visible={confirmCloseIssueModalVisible}
        confirmText={'Are you sure you want to close this issue?'}
        onConfirmClick={onCloseIssueConfirmed}
        onCancelClick={() => setConfirmCloseIssueModalVisible(false)}
      />
    </div>
  )
}

export default ProtectIssueDetails

const useStyles = createUseStyles({
  container: {
    '& h1': {
      marginBottom: titleMarginBottom
    }
  },
  actionBar: {
    marginTop: 20,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  investigationContainer: {
    marginTop: 30,
    display: 'flex',
    flexDirection: 'column',
    rowGap: 20,
    alignItems: 'right'
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'right',
    columnGap: 10
  }
})